import * as React from 'react';

import { useCreateFunnelFromTemplate, useUpdateFunnelWithRouting } from '@/lib/api/funnels';
import { trpc } from '@/lib/api/trpc';

import type { AutoDeployContext, AutoDeployResult, AutoDeployStarter } from './StudioAutoDeployFrame';

// ============================================================================
// Types
// ============================================================================

export type FunnelNodeLite = {
    id: string;
    name: string;
    type: string;
    kind?: string;
    config?: { instanceId?: string } & Record<string, any>;
    variants?: Array<{ id: string; config?: { instanceId?: string } & Record<string, any> }>;
    [key: string]: any;
};

export type CreatedFunnel = {
    id: string;
    name: string;
    config?: { nodes?: FunnelNodeLite[]; [k: string]: any };
    [k: string]: any;
};

export type OrchestrationState =
    | { phase: 'idle' }
    | { phase: 'creating' }
    | { phase: 'preparing'; funnel: CreatedFunnel }
    | {
        // All deployable steps run concurrently (rolling window capped at
        // DEPLOY_CONCURRENCY). Each step's deployment ids land in `results`
        // keyed by stepId; the funnel config is persisted ONCE at the end.
        phase: 'deploying-parallel';
        funnel: CreatedFunnel;
        queue: FunnelNodeLite[];
        starters: Record<string, AutoDeployStarter>;
        sessionToken: string;
        results: Record<string, AutoDeployResult>;
    }
    | { phase: 'saving'; funnel: CreatedFunnel; total: number }
    | { phase: 'done'; funnel: CreatedFunnel }
    | { phase: 'error'; message: string };

export interface ProgressInfo {
    done: number;
    total: number;
    currentName: string | null;
}

export interface AutoDeployFrameProps {
    key: string;
    context: AutoDeployContext;
    starter: AutoDeployStarter;
    onComplete: (result: AutoDeployResult) => void;
    onError: (message: string) => void;
}

interface UseFunnelCreationParams {
    storeId: string;
    mountPoint?: string;
    /** Pass-through to the starter resolver — picks the digital-product checkout variant. */
    digitalProduct?: boolean;
    /** Fallback message used when an exception has no .message of its own. */
    fallbackErrorMessage?: string;
    /** Override the auto-deploy starter for specific step types
     *  (e.g. `{ checkout: 'bogo-checkout' }`). Unmapped step types fall back
     *  to the per-stepType default. */
    starterOverrides?: Record<string, string>;
    /** Mutate the freshly created funnel before the auto-deploy queue runs.
     *  Useful for wizard-driven flows that need to strip / split / overlay
     *  steps based on user answers. The callback is responsible for
     *  persisting any changes (via updateFunnelWithRouting) and returning
     *  the up-to-date funnel; the hook then re-derives the bare-node queue
     *  from whatever shape the callback returned. */
    prepareFunnel?: (funnel: CreatedFunnel) => Promise<CreatedFunnel>;
}

const ROUTABLE_STEP_TYPES = new Set(['checkout', 'thankyou', 'offer', 'upsell', 'downsell', 'landing']);

/** How many Studio auto-deploy iframes may run at the same time. Each iframe
 *  loads the full Studio app + bundles + uploads, so this is capped to keep
 *  the browser stable while still collapsing N sequential deploys into a few
 *  concurrent batches. */
const DEPLOY_CONCURRENCY = 3;

// ============================================================================
// Pure helpers (exported so consumers can re-derive progress, etc.)
// ============================================================================

export function listBareRoutableNodes(funnel: CreatedFunnel): FunnelNodeLite[] {
    const nodes = funnel.config?.nodes ?? [];
    return nodes.filter((node) => {
        if (!node || node.kind === 'condition') return false;
        if (!ROUTABLE_STEP_TYPES.has(node.type)) return false;
        if (node.config?.instanceId) return false;
        // Split steps (weighted A/B or geo) — the merchant fills variant
        // content via Studio after creation, so the auto-deploy queue must
        // not run for them. The presence of splitType is the signal.
        if (node.splitType && Array.isArray(node.variants) && node.variants.length > 0) {
            return false;
        }
        if (Array.isArray(node.variants) && node.variants.length > 0) {
            return node.variants.some((v) => !v.config?.instanceId);
        }
        return true;
    });
}

function applyDeploymentToNode(
    node: FunnelNodeLite,
    deployment: AutoDeployResult,
): FunnelNodeLite {
    const existing = node.config ?? {};
    // A node with no path fails server validation and blocks the funnel save
    // (TAG-1946). `metadata.internalPath` only: templates seed a DISTINCT
    // `config.path` per step (`/offer-eu/:orderId`, `/offer-us/:orderId`) while
    // several of them auto-deploy from the SAME starter, so writing the starter's
    // path to `pagePath` — which resolveStepPath reads ahead of `config.path` —
    // would collapse those steps onto one path and fail duplicate-path validation.
    const internalPath = existing.metadata?.internalPath || deployment.pagePath;
    const nextConfig = {
        ...existing,
        pluginId: deployment.pluginId,
        instanceId: deployment.instanceId ?? existing.instanceId,
        instanceVersion: '1.0.0',
        variant: existing.variant || 'Default',
        metadata: {
            ...(existing.metadata ?? {}),
            editorType: 'studio',
            pluginId: deployment.pluginId,
            deploymentId: deployment.deploymentId,
            ...(internalPath ? { internalPath } : {}),
        },
    };
    return { ...node, config: nextConfig };
}

function mergeNodeIntoFunnel(funnel: CreatedFunnel, updated: FunnelNodeLite): CreatedFunnel {
    const nodes = funnel.config?.nodes ?? [];
    return {
        ...funnel,
        config: {
            ...(funnel.config ?? {}),
            nodes: nodes.map((n) => (n.id === updated.id ? updated : n)),
        },
    };
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Drives the create-funnel-from-template orchestration: create the funnel,
 * prefetch every bare routable step's canonical Studio starter, then deploy
 * them CONCURRENTLY (rolling window of `DEPLOY_CONCURRENCY` hidden iframes).
 * Each deploy is independent — it returns its own plugin/deployment/instance
 * ids — so the only shared state is the funnel config, which is persisted a
 * single time once every step has reported back (avoiding the lost-update
 * race that per-step saves would create).
 *
 * Consumers render one `<StudioAutoDeployFrame {...props} />` per entry in
 * `autoDeployFramePropsList`. They also pick UI off `orchestration.phase`
 * (typically: `idle` → flat list, `error` → retry pane, anything else →
 * progress bar).
 */
export function useFunnelCreation({
    storeId,
    mountPoint = 'default',
    digitalProduct,
    fallbackErrorMessage = 'Failed to create funnel',
    starterOverrides,
    prepareFunnel,
}: UseFunnelCreationParams) {
    const [orchestration, setOrchestration] = React.useState<OrchestrationState>({ phase: 'idle' });

    const createMutation = useCreateFunnelFromTemplate();
    const updateMutation = useUpdateFunnelWithRouting();
    const createBuilderSession = trpc.builder.createSession.useMutation();
    const trpcUtils = trpc.useUtils();

    // Guards the single batched save so it fires exactly once even though the
    // effect below re-runs on every `results` update (and twice under React
    // StrictMode in dev).
    const savingStartedRef = React.useRef(false);

    const handleAutoDeployError = React.useCallback((message: string) => {
        setOrchestration({ phase: 'error', message });
    }, []);

    const handleStepComplete = React.useCallback((stepId: string, deployment: AutoDeployResult) => {
        // Functional updater so two iframes finishing at nearly the same time
        // both merge into the latest `results` instead of clobbering each other.
        setOrchestration((prev) => {
            if (prev.phase !== 'deploying-parallel') return prev;
            if (prev.results[stepId]) return prev;
            return { ...prev, results: { ...prev.results, [stepId]: deployment } };
        });
    }, []);

    // Batched final save: once every deployable step has reported its ids,
    // fold them all into the funnel config and persist with a single
    // updateWithRouting call.
    React.useEffect(() => {
        if (orchestration.phase !== 'deploying-parallel') return;
        const { funnel, queue, results } = orchestration;
        if (Object.keys(results).length < queue.length) return;
        if (savingStartedRef.current) return;
        savingStartedRef.current = true;

        let mergedFunnel = funnel;
        for (const step of queue) {
            const deployment = results[step.id];
            if (!deployment) continue;
            mergedFunnel = mergeNodeIntoFunnel(mergedFunnel, applyDeploymentToNode(step, deployment));
        }

        setOrchestration({ phase: 'saving', funnel: mergedFunnel, total: queue.length });

        void (async () => {
            try {
                await updateMutation.mutateAsync({
                    funnelId: mergedFunnel.id,
                    storeId,
                    config: mergedFunnel.config as any,
                });
                setOrchestration({ phase: 'done', funnel: mergedFunnel });
            } catch (error) {
                setOrchestration({
                    phase: 'error',
                    message: error instanceof Error ? error.message : fallbackErrorMessage,
                });
            }
        })();
    }, [orchestration, storeId, updateMutation, fallbackErrorMessage]);

    const createFromTemplate = React.useCallback(
        async (templateId: string) => {
            if (!templateId || !storeId) return;
            savingStartedRef.current = false;
            setOrchestration({ phase: 'creating' });

            try {
                const created = (await createMutation.mutateAsync({
                    storeId,
                    templateId,
                    mountPoint,
                })) as unknown as CreatedFunnel;

                // Caller-provided shape transforms (e.g. wizard strips/splits
                // steps + writes paymentSettings before any auto-deploy runs).
                // The callback persists its own changes so the funnel that
                // comes back already matches the queue we walk below.
                setOrchestration({ phase: 'preparing', funnel: created });
                const funnel = prepareFunnel ? await prepareFunnel(created) : created;

                const bareQueue = listBareRoutableNodes(funnel);
                if (bareQueue.length === 0) {
                    setOrchestration({ phase: 'done', funnel });
                    return;
                }

                setOrchestration({ phase: 'preparing', funnel });

                // Prefetch every step's starter in parallel. Steps with no
                // Studio default are dropped (left bare), matching the prior
                // per-step behaviour.
                const starterResults = await Promise.all(
                    bareQueue.map(async (step) => {
                        const overrideStarterId = starterOverrides?.[step.type];
                        const starter = await trpcUtils.funnels.getDefaultStarterContent.fetch({
                            stepType: step.type,
                            digitalProduct: digitalProduct ?? undefined,
                            starterId: overrideStarterId,
                        });
                        return { step, starter };
                    }),
                );

                const queue: FunnelNodeLite[] = [];
                const starters: Record<string, AutoDeployStarter> = {};
                for (const { step, starter } of starterResults) {
                    if (!starter) {
                        console.warn(
                            `[useFunnelCreation] No Studio default for step type "${step.type}" — leaving bare`,
                        );
                        continue;
                    }
                    queue.push(step);
                    starters[step.id] = {
                        starterId: starter.id,
                        starterName: starter.name,
                        category: starter.category,
                        thumbnail: (starter as { thumbnail?: string }).thumbnail,
                        document: starter.document,
                    };
                }

                if (queue.length === 0) {
                    setOrchestration({ phase: 'done', funnel });
                    return;
                }

                const session = await createBuilderSession.mutateAsync({
                    pluginId: '',
                    deploymentId: '',
                    storeId,
                });

                setOrchestration({
                    phase: 'deploying-parallel',
                    funnel,
                    queue,
                    starters,
                    sessionToken: session.token,
                    results: {},
                });
            } catch (error) {
                setOrchestration({
                    phase: 'error',
                    message: error instanceof Error ? error.message : fallbackErrorMessage,
                });
            }
        },
        [
            storeId,
            mountPoint,
            createMutation,
            createBuilderSession,
            trpcUtils,
            digitalProduct,
            starterOverrides,
            fallbackErrorMessage,
            prepareFunnel,
        ],
    );

    const reset = React.useCallback(() => {
        savingStartedRef.current = false;
        setOrchestration({ phase: 'idle' });
    }, []);

    // Per-step progress info, derived from orchestration state.
    let progressInfo: ProgressInfo | null = null;
    if (orchestration.phase === 'deploying-parallel') {
        progressInfo = {
            done: Object.keys(orchestration.results).length,
            total: orchestration.queue.length,
            currentName: null,
        };
    } else if (orchestration.phase === 'saving') {
        progressInfo = { done: orchestration.total, total: orchestration.total, currentName: null };
    } else if (orchestration.phase === 'preparing') {
        const funnelQueue = listBareRoutableNodes(orchestration.funnel);
        progressInfo = { done: 0, total: funnelQueue.length, currentName: null };
    }

    const isBusy = orchestration.phase !== 'idle' && orchestration.phase !== 'error';

    // Rolling window of frames to mount right now — the first up-to-CAP steps
    // that have not yet reported a deployment. Because `pending` preserves
    // queue order and an in-flight step only leaves it once it completes,
    // already-mounted iframes keep stable React keys; a fresh step rolls into
    // the freed slot on the next render.
    let autoDeployFramePropsList: AutoDeployFrameProps[] = [];
    let currentThumbnail: string | undefined;
    if (orchestration.phase === 'deploying-parallel') {
        const { funnel, queue, starters, sessionToken, results } = orchestration;
        const pending = queue.filter((step) => !results[step.id]);
        currentThumbnail = pending.length > 0 ? starters[pending[0].id]?.thumbnail : undefined;
        autoDeployFramePropsList = pending.slice(0, DEPLOY_CONCURRENCY).map((step) => ({
            key: `${funnel.id}:${step.id}`,
            context: {
                storeId,
                funnelId: funnel.id,
                stepId: step.id,
                stepType: step.type,
                sessionToken,
            },
            starter: starters[step.id],
            onComplete: (result: AutoDeployResult) => handleStepComplete(step.id, result),
            onError: handleAutoDeployError,
        }));
    }

    return {
        orchestration,
        progressInfo,
        isBusy,
        createFromTemplate,
        reset,
        autoDeployFramePropsList,
        currentThumbnail,
    };
}
